import React, { useContext, useEffect, useMemo, useState, useRef, useCallback } from 'react';
import { tabulatorGet, downloadBlobGet, fetchTextGet, deleteRequest, getRequest } from '../../../api/ApiController';
import { DatabaseContext } from '../../../redux/DatabaseStore';
import { ReactTabulator, reactFormatter } from 'react-tabulator';
import '../../../assets/css/style.css';
import '../../../assets/css/iconsmenu.css';
import { confirmDialog } from 'primereact/confirmdialog';
import { Dialog } from 'primereact/dialog';
import { PanelMenu } from 'primereact/panelmenu';
import { Checkbox } from 'primereact/checkbox';
import { Avatar } from 'primereact/avatar';
import "primereact/resources/themes/lara-light-indigo/theme.css";
import "primereact/resources/primereact.min.css";
import "primeicons/primeicons.css";
import { MdSearch } from "react-icons/md";
import { MdSearchOff } from "react-icons/md";
import { AddIcon, EditIcon, CopyIcon, DeleteIcon, PrintIcon, SearchIcon, ExportIcon, RefreshIcon, VerticalLineIcon, BackToFirstIcon, BackIcon, ForwardIcon, ForwardToLastIcon, FavoriteIcon, PriceIcon, EditRowIcon } from "../../svg/svgIcons";
import { DateTime } from "luxon";
import UtilContext from '../../../components/UtilContext';
import { OverflowMenuProvider } from '../../../components/toolbar/OverflowMenu';
import IntersectionObserverWrapper from '../../../components/toolbar/IntersectionObserverWrap';
import { scrollToFirst as scrollToFirstUtil, scrollUp as scrollUpUtil, scrollDown as scrollDownUtil, scrollToLast as scrollToLastUtil } from '../../../utils/tabulatorScroll';
import { resetTabulatorMask } from '../../../utils/tabulatorResetMask';
import { LocaleContext } from '../../../components/LocaleContext';
import { getEffectiveMask } from '../../../utils/effectiveMask';
import { cleanSorters, coerceHeaderFilterList, extractSafePaginationParams, createDedupedAjaxRequest, safeParse, parseSorters, parseMaskFilters, mergePagedStandardListFiltersAndSorters, sanitizeTabulatorLayoutAttributes, TABULATOR_GROUP_A_FLAT_REMOTE_LAYOUT_SANITIZE_OPTIONS } from '../../../utils/tabulatorProgressiveLoading';
import { dateUserHeaderFilter } from '../../../utils/dateUserHeaderFilter';
import { inputOnEnterHeaderFilter } from '../../../utils/inputOnEnterHeaderFilter';
import { tabulatorColumnMinWidthFromTitle, tabulatorDateUserColumnMinWidth } from '../../../utils/tabulatorColumnMinWidth';
import { buildTabulatorIdColumn } from '../../../utils/tabulatorIdColumn';
import { TABULATOR_AUDIT_MIN_ROW_HEIGHT } from '../../../utils/tabulatorAuditColumns';
import { getPusherChannel } from '../../../utils/pusherChannel';
import {
  TRAINING_INVITATION_LIST_REMOVED_EVENT,
  TRAINING_INVITATION_LIST_UPDATED_EVENT,
  SESSION_SAFE_UPDATE,
  applyPagedMutationOverlay,
  classifyTrainingInvitationListMutation,
} from '../../../utils/trainingInvitationListSync';
import { buildExportQueryStringFromParams, openPrintHtmlWindow, triggerBlobDownload } from '../../../utils/serviceTaskExport';
import { xlsxDownloadFilename } from '../../../utils/exportExcelNaming';
import PrintOptionsDialog from '../../../components/PrintOptionsDialog';
import { applyTabulatorRuntimeI18n } from '../../../utils/tabulatorRuntimeI18n';
const TRAINING_INVITATION_LIST_COLUMN_I18N_KEYS = Object.freeze({
  "name": "TrainingInvitationList.Column.Name",
  "invitation_date": "TrainingInvitationList.Column.Date",
  "invitation_time": "TrainingInvitationList.Column.Time",
  "training.name": "TrainingInvitationList.Column.Training",
  "customs_state": "TrainingInvitationList.Column.Status",
  "company.name": "TrainingInvitationList.Column.Company",
  "machines": "Common.Machine",
  "company_contact.lecturer.name": "TrainingInvitationList.Column.Lecturer",
  "custom_company_contact_name": "TrainingInvitationList.Column.Trainer",
  "custom_employees": "TrainingInvitationList.Column.Employees",
  "created_at": "EmployeeList.Column.CreatedAt",
  "updated_at": "EmployeeList.Column.UpdatedAt",
  "id": "EmployeeList.Column.Id",
});


const PAGE_LIST_KEY = 'TrainingInvitationList';
const STATUS_ACTIVE = 'Aktívna';
const STATUS_INACTIVE = 'Neaktívna';

const DEFAULT_COLUMNS_VISIBLE = Object.freeze({
  id: true,
  name: true,
  invitation_date: true,
  invitation_time: true,
  'training.name': true,
  customs_state: true,
  'company.name': true,
  machines: true,
  'company_contact.lecturer.name': true,
  custom_company_contact_name: true,
  custom_employees: true,
  created_at: true,
  updated_at: true,
});

function mergeVisibility(saved) {
  const merged = { ...DEFAULT_COLUMNS_VISIBLE };
  if (saved && typeof saved === 'object') {
    Object.keys(DEFAULT_COLUMNS_VISIBLE).forEach((k) => {
      if (Object.prototype.hasOwnProperty.call(saved, k)) merged[k] = saved[k] !== false;
    });
  }
  if (!Object.values(merged).some(Boolean)) return { ...DEFAULT_COLUMNS_VISIBLE };
  return merged;
}

function applyDataColumnVisibilityToTable(table, visibilityMap) {
  if (!table) return;
  for (const f of Object.keys(DEFAULT_COLUMNS_VISIBLE)) {
    if (visibilityMap[f] === false) table.hideColumn?.(f);
    else table.showColumn?.(f);
  }
  setTimeout(() => table.redraw?.(true), 0);
}

function layoutAttributesIncludeId(attributes) {
  if (!Array.isArray(attributes)) return false;
  return attributes.some((node) => node && typeof node === 'object' && node.field === 'id');
}

function statusHeaderFilterParams(t) {
  return {
    values: {
      [STATUS_ACTIVE]: t('TrainingInvitationList.Status.Active'),
      [STATUS_INACTIVE]: t('TrainingInvitationList.Status.Inactive'),
    },
    clearable: true,
  };
}

export default function TrainingInvitationList({ preview = null, preview_period = null }) {
  const [state, dispatch] = useContext(DatabaseContext);
  const { t, locale } = useContext(LocaleContext);
  const { setUserMaskFilter, openFavoriteDialog, toast, setFastView, getDateFormat, formatDate, getUserColor, getUserInitials, openChosenNotificationPreferences, setDialogText, setVisible } = useContext(UtilContext);
  const dateFmt = getDateFormat?.(state?.module_settings) ?? 'dd.MM.yyyy';
  const dateTimeFmt = `${dateFmt} HH:mm`;

  const pageSizePc = useMemo(() => {
    const v = (state.module_settings || []).find(s => s.module === 'global' && s.key === 'page_size_pc')?.value;
    const n = parseInt(v, 10);
    return (Number.isFinite(n) && n > 0) ? n : 50;
  }, [state.module_settings]);

  const [editIcon, setEditIcon] = useState(true);
  const [deleteIcon, setDeleteIcon] = useState(true);
  const [favoriteIcon] = useState(false);
  const [copyIcon, setCopyIcon] = useState(true);

  const [visibleSearch, setVisibleSearch] = useState(false);
  const [searchIconColor, setSearchIconColor] = useState('');
  const [searchValue, setSearchValue] = useState('');

  const [markUnmark, setMarkUnmarked] = useState(true);
  const [columnsVisible, setColumnsVisible] = useState({ ...DEFAULT_COLUMNS_VISIBLE });
  const [maskVisible, setMaskVisible] = useState(false);
  const [printChoiceVisible, setPrintChoiceVisible] = useState(false);
  const [printOrientationChoice, setPrintOrientationChoice] = useState('landscape');
  const [printLayoutChoice, setPrintLayoutChoice] = useState('branded');
  const [tableSessionKey, setTableSessionKey] = useState(0);

  const ref = useRef(null);
  const tableContainerRef = useRef(null);
  const overlayRef = useRef(null);
  const inFlightRef = useRef({ sig: null, promise: null });
  const completedCacheRef = useRef(new Map());
  const tableBuiltRef = useRef(false);
  const lastResponseRef = useRef(null);
  const isRestoringRef = useRef(false);
  const userInteractedRef = useRef(false);
  const savedHScrollRef = useRef({ left: 0, pending: false });
  const lastContainerHeightRef = useRef(0);
  const needsRedrawRef = useRef(false);
  const pendingRowIdsRef = useRef(new Set());
  const rafRedrawRef = useRef(null);
  const submittedSearchValueRef = useRef('');
  const pendingInvalidationRef = useRef(false);
  const mutationOverlayRef = useRef(new Map());
  const listMountedRef = useRef(true);
  const restartQueuedRef = useRef(false);
  const ajaxGenerationRef = useRef(0);
  const confirmedQueryRef = useRef({ filters: [], sorters: [] });
  const sessionQueryActiveRef = useRef(false);

  const getTable = useCallback(() => ref.current?.table ?? ref.current, []);

  const isTableVisible = useCallback(() => {
    const el = tableContainerRef.current;
    if (el && el.offsetParent != null && el.clientHeight > 0) return true;
    const table = getTable();
    const tel = table?.getElement?.() ?? table?.element;
    return !!(tel && tel.offsetParent != null && tel.clientHeight > 0);
  }, [getTable]);

  const snapshotConfirmedQuery = useCallback(() => {
    const table = getTable();
    confirmedQueryRef.current = {
      filters: coerceHeaderFilterList(table?.getHeaderFilters?.()),
      sorters: cleanSorters(table?.getSorters?.() || []),
    };
  }, [getTable]);

  const restartFullProgressiveSession = useCallback(() => {
    if (!listMountedRef.current || restartQueuedRef.current) return;
    snapshotConfirmedQuery();
    sessionQueryActiveRef.current = true;
    ajaxGenerationRef.current += 1;
    pendingInvalidationRef.current = false;
    mutationOverlayRef.current = new Map();
    restartQueuedRef.current = true;
    completedCacheRef.current.clear();
    lastResponseRef.current = null;
    inFlightRef.current = { sig: null, promise: null };
    tableBuiltRef.current = false;
    isRestoringRef.current = true;
    const table = getTable();
    if (typeof table?.destroy === 'function') table.destroy();
    ref.current = null;
    setTableSessionKey((key) => key + 1);
  }, [getTable, snapshotConfirmedQuery]);

  const requestFullProgressiveSessionRestart = useCallback(() => {
    if (!listMountedRef.current) return;
    if (!isTableVisible()) {
      pendingInvalidationRef.current = true;
      return;
    }
    restartFullProgressiveSession();
  }, [isTableVisible, restartFullProgressiveSession]);

  const getLoadedRowComponent = useCallback((id) => {
    const table = getTable();
    if (!table || typeof table.getRow !== 'function' || id == null || id === '') return null;
    try {
      const row = table.getRow(id);
      return row && row !== false ? row : null;
    } catch (_e) {
      return null;
    }
  }, [getTable]);

  const scheduleRedraw = useCallback(() => {
    if (rafRedrawRef.current != null) return;
    rafRedrawRef.current = requestAnimationFrame(() => {
      const table = getTable();
      rafRedrawRef.current = null;
      if (!table) return;
      const ids = Array.from(pendingRowIdsRef.current);
      pendingRowIdsRef.current.clear();
      needsRedrawRef.current = false;
      ids.forEach((id) => {
        const row = table.getRow?.(id);
        if (!row) return;
        row.reformat?.();
      });
    });
  }, [getTable]);

  const applySafeInvitationPatch = useCallback((detail) => {
    const canonicalRow = detail?.row && typeof detail.row === 'object' && !Array.isArray(detail.row)
      ? (detail.row.data && detail.row.data.id != null ? detail.row.data : detail.row)
      : null;
    const id = detail?.id ?? canonicalRow?.id;
    const rowComponent = getLoadedRowComponent(id);
    if (!rowComponent || typeof rowComponent.update !== 'function' || !canonicalRow) {
      requestFullProgressiveSessionRestart();
      return;
    }
    rowComponent.update(canonicalRow);
    mutationOverlayRef.current.set(String(id), canonicalRow);
    pendingRowIdsRef.current.add(id);
    if (isTableVisible()) scheduleRedraw();
    else needsRedrawRef.current = true;
  }, [getLoadedRowComponent, isTableVisible, requestFullProgressiveSessionRestart, scheduleRedraw]);

  const captureHScroll = useCallback(() => {
    const table = getTable();
    if (!table) return;
    const holder = table.rowManager?.element;
    if (!holder) return;
    savedHScrollRef.current = { left: holder.scrollLeft, pending: true };
  }, [getTable]);

  const restoreHScroll = useCallback(() => {
    if (!savedHScrollRef.current.pending) return;
    const table = getTable();
    if (!table) return;
    const holder = table.rowManager?.element;
    const header = table.columnManager?.contentsElement;
    if (!holder) return;
    const left = savedHScrollRef.current.left;
    requestAnimationFrame(() => {
      requestAnimationFrame(() => {
        holder.scrollLeft = left;
        if (header) header.scrollLeft = left;
        savedHScrollRef.current.pending = false;
      });
    });
  }, [getTable]);

  useEffect(() => {
    listMountedRef.current = true;
    return () => {
      listMountedRef.current = false;
      if (rafRedrawRef.current != null) {
        cancelAnimationFrame(rafRedrawRef.current);
        rafRedrawRef.current = null;
      }
    };
  }, []);

  useEffect(() => {
    const el = tableContainerRef.current;
    if (!el || typeof ResizeObserver === 'undefined') return;
    const ro = new ResizeObserver(() => {
      const h = el.clientHeight;
      const visible = h > 0 || isTableVisible();
      if (visible) {
        const wasHidden = lastContainerHeightRef.current <= 0;
        lastContainerHeightRef.current = Math.max(h, 1);
        if (pendingInvalidationRef.current) {
          requestAnimationFrame(() => restartFullProgressiveSession());
        } else if (wasHidden) {
          requestAnimationFrame(() => getTable()?.redraw?.(true));
        } else if (needsRedrawRef.current && isTableVisible()) {
          scheduleRedraw();
        }
      } else {
        lastContainerHeightRef.current = 0;
      }
    });
    ro.observe(el);
    return () => ro.disconnect();
  }, [getTable, isTableVisible, restartFullProgressiveSession, scheduleRedraw]);

  useEffect(() => {
    const onUpdated = (event) => {
      const decision = classifyTrainingInvitationListMutation(event);
      if (decision === SESSION_SAFE_UPDATE) {
        applySafeInvitationPatch(event.detail);
        return;
      }
      requestFullProgressiveSessionRestart();
    };
    const onRemoved = () => {
      requestFullProgressiveSessionRestart();
    };
    window.addEventListener(TRAINING_INVITATION_LIST_UPDATED_EVENT, onUpdated);
    window.addEventListener(TRAINING_INVITATION_LIST_REMOVED_EVENT, onRemoved);
    return () => {
      window.removeEventListener(TRAINING_INVITATION_LIST_UPDATED_EVENT, onUpdated);
      window.removeEventListener(TRAINING_INVITATION_LIST_REMOVED_EVENT, onRemoved);
    };
  }, [applySafeInvitationPatch, requestFullProgressiveSessionRestart]);

  // Pusher payloads are id-only → fetch canonical show; create/unknown membership → invalidate.
  useEffect(() => {
    const channel = getPusherChannel();
    if (!channel) return;
    const DEDUPE_MS = 800;
    const lastHandled = { key: '', at: 0 };
    const shouldSkip = (eventType, id) => {
      if (id == null) return true;
      const key = `${eventType}-${id}`;
      const now = Date.now();
      if (lastHandled.key === key && now - lastHandled.at < DEDUPE_MS) return true;
      lastHandled.key = key;
      lastHandled.at = now;
      return false;
    };
    const invalidateFromRemote = () => requestFullProgressiveSessionRestart();
    const onCreated = (data) => {
      const id = data?.id ?? null;
      if (id == null || shouldSkip('training_invitation.created', id)) return;
      invalidateFromRemote();
    };
    const onUpdated = (data) => {
      const id = data?.id ?? null;
      if (id == null || shouldSkip('training_invitation.updated', id)) return;
      getRequest(`/api/training_invitations/${id}`).then((res) => {
        const rowData = res?.data ?? res;
        if (!rowData?.id) {
          invalidateFromRemote();
          return;
        }
        const decision = classifyTrainingInvitationListMutation({
          detail: { operation: 'update', row: rowData, changedFields: ['updated_at'] },
        });
        if (decision === SESSION_SAFE_UPDATE && getLoadedRowComponent(rowData.id)) {
          applySafeInvitationPatch({ operation: 'update', id: rowData.id, row: rowData });
          return;
        }
        invalidateFromRemote();
      }).catch(() => invalidateFromRemote());
    };
    const onDeleted = (data) => {
      const id = data?.id ?? null;
      if (id == null || shouldSkip('training_invitation.deleted', id)) return;
      invalidateFromRemote();
    };
    channel.bind('training_invitation.created', onCreated);
    channel.bind('training_invitation.updated', onUpdated);
    channel.bind('training_invitation.deleted', onDeleted);
    return () => {
      channel.unbind('training_invitation.created', onCreated);
      channel.unbind('training_invitation.updated', onUpdated);
      channel.unbind('training_invitation.deleted', onDeleted);
    };
  }, [applySafeInvitationPatch, getLoadedRowComponent, requestFullProgressiveSessionRestart]);

  function searchTable() {
    const normalized = String(searchValue ?? '').trim();
    submittedSearchValueRef.current = normalized;
    setSearchIconColor(normalized === '' ? '' : 'active');
    captureHScroll();
    requestFullProgressiveSessionRestart();
    setVisibleSearch(false);
  }

  const clearSearch = () => {
    setSearchIconColor('');
    setSearchValue('');
    submittedSearchValueRef.current = '';
    captureHScroll();
    requestFullProgressiveSessionRestart();
  };

  const accept = () => {
    const rowsToDelete = getTable()?.getSelectedData?.() ?? [];
    const promises = rowsToDelete.map((element) => deleteRequest(`/api/training_invitations/${element.id}`));
    Promise.allSettled(promises).then((results) => {
      const ok = results.filter((r) => r.status === 'fulfilled').length;
      const fail = results.length - ok;
      if (ok > 0) {
        toast?.current?.show({ severity: 'info', summary: t('TrainingInvitationList.Toast.DeletedSummary'), life: 3000, detail: t('TrainingInvitationList.Toast.DeletedDetail') });
      }
      if (fail > 0) {
        toast?.current?.show({ severity: 'warn', summary: t('TrainingInvitationList.Toast.RejectedSummary'), detail: t('TrainingInvitationList.Toast.DeleteFailedDetail'), life: 3000 });
      }
      requestFullProgressiveSessionRestart();
    });
  };

  const reject = () => {
    toast?.current?.show({ severity: 'warn', summary: t('TrainingInvitationList.Toast.RejectedSummary'), detail: t('TrainingInvitationList.Toast.RequestCancelled'), life: 3000 });
  };

  const confirm = () => {
    confirmDialog({
      message: t('TrainingInvitationList.ConfirmDeleteMessage'),
      header: t('Common.ConfirmDelete'),
      icon: 'pi pi-info-circle',
      acceptClassName: 'p-button-danger',
      acceptLabel: t('Common.No'),
      rejectLabel: t('Common.Yes'),
      accept: reject,
      reject: accept,
    });
  };

  const rowClick = () => {};

  const onSelectRow = (data) => {
    const empty = !data || data.length === 0;
    setEditIcon(empty);
    setCopyIcon(empty);
    setDeleteIcon(empty);
  };

  function onEditRow(invitation) {
    dispatch({ type: 'create_new_tab', name: t('TrainingInvitationList.Tab.Edit'), translationKey: 'TrainingInvitationList.Tab.Edit', legacyLabel: t('TrainingInvitationList.Tab.Edit'), page: 'TrainingInvitationAdd', props: { invitation, type: 'edit' }, mask: 'trng028' });
  }

  function EditRowFormatter(props) {
    const rowData = props.cell.getRow().getData();
    return <div className='flex items-center justify-center w-full h-full'><button type='button' onClick={() => onEditRow(rowData)}><EditRowIcon /></button></div>;
  }

  function EmployeesFormatter(props) {
    const rowData = props.cell.getRow().getData();
    const employees = rowData?.employees || [];
    return employees.map((item, idx) => {
      let initials = '';
      const allNames = String(item?.name || '').trim().split(' ').filter(Boolean);
      if (allNames.length === 3) {
        initials = allNames.reduce((acc, curr, index) => {
          if (index === allNames.length - 2 || index === allNames.length - 1) acc = `${acc}${curr.charAt(0).toUpperCase()}`;
          return acc;
        }, '');
      } else {
        initials = allNames.reduce((acc, curr, index) => {
          if (index === 0 || index === allNames.length - 1) acc = `${acc}${curr.charAt(0).toUpperCase()}`;
          return acc;
        }, '');
      }
      return <Avatar key={item?.id ?? idx} title={item?.name} label={initials} shape="circle" style={{ backgroundColor: '#1d4ed8', color: '#ffffff', marginRight: '5px' }} />;
    });
  }

  function StateFormatter(props) {
    const rowData = props.cell.getRow().getData();
    if (new Date(rowData.invitation_date) < new Date()) return t('TrainingInvitationList.Status.Inactive');
    return t('TrainingInvitationList.Status.Active');
  }

  function MachineName(props) {
    const rowData = props.cell.getRow().getData();
    return rowData?.machines?.map((machine) => machine?.name).filter(Boolean).join('/') || '';
  }

  function CompanyContactName(props) {
    const rowData = props.cell.getRow().getData();
    if (rowData?.company_contact?.employee_id) return rowData?.company_contact?.employee?.name || '';
    return rowData?.company_contact?.name || '';
  }

  function auditDateText(raw) {
    if (raw == null || raw === '') return '';
    if (formatDate) {
      const text = formatDate(raw, dateTimeFmt, true);
      return text || '';
    }
    const dt = DateTime.fromISO(String(raw));
    return dt.isValid ? dt.toFormat(dateTimeFmt) : '';
  }

  function CreatedAtFormatter(props) {
    const rowData = props.cell.getRow().getData();
    const u = rowData?.created_by_user;
    const displayName = u?.employee?.name || u?.username || '';
    const text = auditDateText(rowData?.created_at);
    return (
      <div className="flex items-center justify-start w-full h-full min-w-0 gap-1">
        {u ? (
          <Avatar
            className="shrink-0"
            title={displayName}
            label={getUserInitials?.(displayName) || ''}
            shape="circle"
            style={{ backgroundColor: getUserColor?.(u?.username) || '#6b7280', color: '#ffffff' }}
          />
        ) : null}
        <div className="min-w-0 flex-1 truncate whitespace-nowrap" title={text || undefined}>{text}</div>
      </div>
    );
  }

  function UpdatedAtFormatter(props) {
    const rowData = props.cell.getRow().getData();
    const u = rowData?.updated_by_user;
    const displayName = u?.employee?.name || u?.username || '';
    const text = auditDateText(rowData?.updated_at);
    return (
      <div className="flex items-center justify-start w-full h-full min-w-0 gap-1">
        {u ? (
          <Avatar
            className="shrink-0"
            title={displayName}
            label={getUserInitials?.(displayName) || ''}
            shape="circle"
            style={{ backgroundColor: getUserColor?.(u?.username) || '#6b7280', color: '#ffffff' }}
          />
        ) : null}
        <div className="min-w-0 flex-1 truncate whitespace-nowrap" title={text || undefined}>{text}</div>
      </div>
    );
  }

  const textHeaderFilter = useCallback((headerValue) => {
    if (!headerValue || String(headerValue).trim() === '') return true;
    return true;
  }, []);

  const dateColumnFilter = useCallback((headerValue) => {
    if (!headerValue || String(headerValue).trim() === '') return true;
    return true;
  }, []);

  const idTitle = t('EmployeeList.Column.Id');
  const createdTitle = t('EmployeeList.Column.CreatedAt');
  const updatedTitle = t('EmployeeList.Column.UpdatedAt');
  const nameTitle = t('TrainingInvitationList.Column.Name');
  const dateTitle = t('TrainingInvitationList.Column.Date');
  const timeTitle = t('TrainingInvitationList.Column.Time');
  const trainingTitle = t('TrainingInvitationList.Column.Training');
  const statusTitle = t('TrainingInvitationList.Column.Status');
  const companyTitle = t('TrainingInvitationList.Column.Company');
  const machineTitle = t('Common.Machine');
  const lecturerTitle = t('TrainingInvitationList.Column.Lecturer');
  const trainerTitle = t('TrainingInvitationList.Column.Trainer');
  const employeesTitle = t('TrainingInvitationList.Column.Employees');

  const columns = useMemo(() => [
    { field: 'rowSelection', formatter: 'rowSelection', titleFormatter: 'rowSelection', hozAlign: 'center', width: 44, minWidth: 44, headerSort: false, resizable: false, frozen: true, print: false, download: false, headerFilter: false },
    { title: '', field: 'editor', hozAlign: 'center', width: 44, minWidth: 44, headerSort: false, formatter: reactFormatter(<EditRowFormatter />), resizable: false, frozen: true, print: false, download: false, headerFilter: false },
    buildTabulatorIdColumn({ frozen: true, headerFilter: inputOnEnterHeaderFilter, headerFilterLiveFilter: false, headerFilterFunc: textHeaderFilter, extra: { title: idTitle } }),
    { title: nameTitle, field: 'name', frozen: true, headerFilter: inputOnEnterHeaderFilter, headerFilterLiveFilter: false, headerFilterFunc: textHeaderFilter, minWidth: tabulatorColumnMinWidthFromTitle(nameTitle), widthGrow: 1 },
    { title: dateTitle, field: 'invitation_date', headerFilter: inputOnEnterHeaderFilter, headerFilterLiveFilter: false, headerFilterFunc: textHeaderFilter, minWidth: tabulatorColumnMinWidthFromTitle(dateTitle), widthGrow: 1, formatter: (cell) => {
      const value = cell.getValue();
      if (!value) return '';
      const dt = DateTime.fromISO(String(value));
      return dt.isValid ? dt.toFormat(dateFmt) : String(value);
    } },
    { title: timeTitle, field: 'invitation_time', headerFilter: inputOnEnterHeaderFilter, headerFilterLiveFilter: false, headerFilterFunc: textHeaderFilter, minWidth: tabulatorColumnMinWidthFromTitle(timeTitle), widthGrow: 1, formatter: (cell) => {
      const value = cell.getValue();
      if (!value) return '';
      const dt = DateTime.fromISO(String(value));
      return dt.isValid ? dt.toFormat('HH:mm') : String(value);
    } },
    { title: trainingTitle, field: 'training.name', headerFilter: inputOnEnterHeaderFilter, headerFilterLiveFilter: false, headerFilterFunc: textHeaderFilter, minWidth: tabulatorColumnMinWidthFromTitle(trainingTitle), widthGrow: 1 },
    { title: statusTitle, field: 'customs_state', headerFilter: 'list', headerFilterParams: statusHeaderFilterParams(t), headerFilterLiveFilter: false, formatter: reactFormatter(<StateFormatter />), minWidth: tabulatorColumnMinWidthFromTitle(statusTitle), widthGrow: 1 },
    { title: companyTitle, field: 'company.name', headerFilter: inputOnEnterHeaderFilter, headerFilterLiveFilter: false, headerFilterFunc: textHeaderFilter, minWidth: tabulatorColumnMinWidthFromTitle(companyTitle), widthGrow: 1 },
    { title: machineTitle, field: 'machines', headerFilter: inputOnEnterHeaderFilter, headerFilterLiveFilter: false, headerFilterFunc: textHeaderFilter, minWidth: tabulatorColumnMinWidthFromTitle(machineTitle), widthGrow: 1, formatter: reactFormatter(<MachineName />) },
    { title: lecturerTitle, field: 'company_contact.lecturer.name', headerFilter: inputOnEnterHeaderFilter, headerFilterLiveFilter: false, headerFilterFunc: textHeaderFilter, minWidth: tabulatorColumnMinWidthFromTitle(lecturerTitle), widthGrow: 1 },
    { title: trainerTitle, field: 'custom_company_contact_name', headerFilter: inputOnEnterHeaderFilter, headerFilterLiveFilter: false, headerFilterFunc: textHeaderFilter, minWidth: tabulatorColumnMinWidthFromTitle(trainerTitle), widthGrow: 1, formatter: reactFormatter(<CompanyContactName />) },
    { title: employeesTitle, field: 'custom_employees', headerFilter: inputOnEnterHeaderFilter, headerFilterLiveFilter: false, headerFilterFunc: textHeaderFilter, minWidth: tabulatorColumnMinWidthFromTitle(employeesTitle), widthGrow: 1, formatter: reactFormatter(<EmployeesFormatter />) },
    { title: createdTitle, field: 'created_at', minWidth: tabulatorDateUserColumnMinWidth(createdTitle), formatter: reactFormatter(<CreatedAtFormatter />), headerFilter: dateUserHeaderFilter, headerFilterLiveFilter: false, headerFilterParams: { dateFormat: dateFmt, dateTimeFormat: dateTimeFmt }, headerFilterFunc: dateColumnFilter, headerFilterFuncParams: { name: 'created_at' }, hozAlign: 'left', sorter: 'datetime', sorterParams: { format: 'iso' } },
    { title: updatedTitle, field: 'updated_at', minWidth: tabulatorDateUserColumnMinWidth(updatedTitle), formatter: reactFormatter(<UpdatedAtFormatter />), headerFilter: dateUserHeaderFilter, headerFilterLiveFilter: false, headerFilterParams: { dateFormat: dateFmt, dateTimeFormat: dateTimeFmt }, headerFilterFunc: dateColumnFilter, headerFilterFuncParams: { name: 'updated_at' }, hozAlign: 'left', sorter: 'datetime', sorterParams: { format: 'iso' } },
  ], [dateFmt, dateTimeFmt, createdTitle, updatedTitle, idTitle, nameTitle, dateTitle, timeTitle, trainingTitle, statusTitle, companyTitle, machineTitle, lecturerTitle, trainerTitle, employeesTitle, t, textHeaderFilter, dateColumnFilter]);

  const appendPreviewScope = useCallback((extra) => {
    if (preview_period?.dateFrom != null && preview_period?.dateTo != null) {
      extra.preview_period_date_from = preview_period.dateFrom;
      extra.preview_period_date_to = preview_period.dateTo;
    } else if (preview != null && preview !== '') {
      extra.preview = preview;
    }
  }, [preview, preview_period]);

  const buildRequestParams = useCallback((paramsFromTabulator = {}) => {
    const extra = { ...extractSafePaginationParams(paramsFromTabulator) };
    if (extra.page == null) extra.page = 1;
    if (extra.size == null) extra.size = pageSizePc;
    appendPreviewScope(extra);
    if (sessionQueryActiveRef.current) {
      coerceHeaderFilterList(confirmedQueryRef.current.filters).forEach((filter) => {
        if (!filter?.field) return;
        const value = filter.value;
        if (value == null || String(value).trim() === '') return;
        extra[`filter_${String(filter.field).replace(/\./g, '_')}`] = value;
      });
      const confirmedSorters = cleanSorters(confirmedQueryRef.current.sorters || []);
      if (confirmedSorters.length > 0) extra.sorters = JSON.stringify(confirmedSorters);
    } else {
      mergePagedStandardListFiltersAndSorters(extra, {
        getTable: tableBuiltRef.current ? getTable : () => null,
        tableBuiltForParams: tableBuiltRef.current,
        userMaskFilters: state.user_mask_filters,
        maskPage: PAGE_LIST_KEY,
        filterKeyStyle: 'dot_underscore',
      });
    }
    const search = String(submittedSearchValueRef.current ?? '').trim();
    if (search !== '') extra.toolbar_search = search;
    return extra;
  }, [appendPreviewScope, getTable, state.user_mask_filters, pageSizePc]);

  
  useEffect(() => {
    const table = typeof getTable === 'function' ? getTable() : null;
    if (!table?.getColumns) return;
    applyTabulatorRuntimeI18n(table, t, {
      columnTitleKeys: TRAINING_INVITATION_LIST_COLUMN_I18N_KEYS,
      headerFilters: {
        customs_state: {
          placeholder: t('TrainingInvitationList.Column.Status'),
          values: {
            [STATUS_ACTIVE]: t('TrainingInvitationList.Status.Active'),
            [STATUS_INACTIVE]: t('TrainingInvitationList.Status.Inactive'),
          },
        },
      },
      placeholder: typeof t === 'function' ? t('Common.NoData') : undefined,
      reformatRows: true,
    });
  }, [locale, t, getTable]);

const buildRequestParamsRef = useRef(buildRequestParams);
  buildRequestParamsRef.current = buildRequestParams;
  const getBuildRequestParams = useCallback((paramsFromTabulator) => buildRequestParamsRef.current(paramsFromTabulator), []);

  const getPrintColumnsOrder = useCallback(() => {
    const table = getTable();
    const allowed = Object.keys(DEFAULT_COLUMNS_VISIBLE).filter((f) => f !== 'id');
    const fallback = () => allowed.filter((f) => columnsVisible[f] !== false);
    if (!table?.getColumns) return fallback();
    const allowedSet = new Set(allowed);
    const out = [];
    const seen = new Set();
    try {
      table.getColumns().forEach((col) => {
        const field = col.getField?.() ?? col.getDefinition?.()?.field;
        if (!field || !allowedSet.has(field)) return;
        if (columnsVisible[field] === false) return;
        if (seen.has(field)) return;
        seen.add(field);
        out.push(field);
      });
    } catch (_) {
      return fallback();
    }
    return out.length > 0 ? out : fallback();
  }, [getTable, columnsVisible]);

  const buildInvitationExportQueryString = useCallback((opts = {}) => {
    const extra = {};
    appendPreviewScope(extra);
    mergePagedStandardListFiltersAndSorters(extra, {
      getTable: tableBuiltRef.current ? getTable : () => null,
      tableBuiltForParams: tableBuiltRef.current,
      userMaskFilters: state.user_mask_filters,
      maskPage: PAGE_LIST_KEY,
      filterKeyStyle: 'dot_underscore',
    });
    const search = String(submittedSearchValueRef.current ?? '').trim();
    if (search !== '') extra.toolbar_search = search;
    return buildExportQueryStringFromParams(extra, opts);
  }, [appendPreviewScope, getTable, state.user_mask_filters]);

  const runInvitationPrint = useCallback(async () => {
    try {
      setPrintChoiceVisible(false);
      setDialogText?.(t('Common.PreparingPrint'));
      setVisible?.(true);
      const suffix = buildInvitationExportQueryString({
        includePrintColumns: true,
        printLayout: printLayoutChoice,
        printOrientation: printOrientationChoice,
        printColumns: getPrintColumnsOrder(),
      });
      const html = await fetchTextGet(`/api/training_invitations/export/print${suffix}`);
      if (!openPrintHtmlWindow(html)) {
        toast?.current?.show({ severity: 'warn', summary: t('Common.Print'), detail: t('Common.EnablePopups'), life: 4000 });
      }
    } catch (e) {
      toast?.current?.show({ severity: 'error', summary: t('Common.Print'), detail: e?.message || t('Common.LoadFailed'), life: 4500 });
    } finally {
      setVisible?.(false);
    }
  }, [buildInvitationExportQueryString, getPrintColumnsOrder, printLayoutChoice, printOrientationChoice, setDialogText, setVisible, t, toast]);

  const exportInvitationsXlsx = useCallback(async () => {
    try {
      setDialogText?.(t('Common.PreparingExport'));
      setVisible?.(true);
      const suffix = buildInvitationExportQueryString({
        includePrintColumns: true,
        printColumns: getPrintColumnsOrder(),
      });
      const defaultXlsx = xlsxDownloadFilename('training-invitations') || t('TrainingInvitationList.ExportFileName');
      const { blob, filename } = await downloadBlobGet(`/api/training_invitations/export/xlsx${suffix}`, defaultXlsx);
      triggerBlobDownload(blob, filename || defaultXlsx);
    } catch (e) {
      toast?.current?.show({ severity: 'error', summary: t('Common.ExportToExcel'), detail: e?.message || t('Common.LoadFailed'), life: 4500 });
    } finally {
      setVisible?.(false);
    }
  }, [buildInvitationExportQueryString, getPrintColumnsOrder, setDialogText, setVisible, t, toast]);

  const restoreHeaderFilters = useCallback((table, rawFilters) => {
    const filterEntries = Array.isArray(rawFilters)
      ? rawFilters
      : (rawFilters && typeof rawFilters === 'object'
        ? Object.entries(rawFilters).map(([field, value]) => ({ field, value }))
        : []);
    if (sessionQueryActiveRef.current && filterEntries.length === 0) {
      table.clearHeaderFilter?.();
      return;
    }
    filterEntries.forEach((filter) => {
      if (!filter?.field) return;
      const value = filter.value;
      if (value == null || value === '') return;
      const def = table.getColumn?.(filter.field)?.getDefinition?.();
      if (!def?.headerFilter) return;
      table.setHeaderFilterValue?.(filter.field, value, true);
    });
  }, []);

  const scrollToFirst = () => scrollToFirstUtil(getTable());
  const scrollUp = () => scrollUpUtil(getTable());
  const scrollDown = () => scrollDownUtil(getTable());
  const scrollToLast = () => scrollToLastUtil(getTable());

  function addNewInvitation() {
    dispatch({ type: 'create_new_tab', name: t('TrainingInvitationList.Tab.New'), translationKey: 'TrainingInvitationList.Tab.New', legacyLabel: t('TrainingInvitationList.Tab.New'), page: 'TrainingInvitationAdd', props: { invitation: null, type: 'add' }, mask: 'trng028' });
  }
  function copyInvitation() {
    dispatch({ type: 'create_new_tab', name: t('TrainingInvitationList.Tab.New'), translationKey: 'TrainingInvitationList.Tab.New', legacyLabel: t('TrainingInvitationList.Tab.New'), page: 'TrainingInvitationAdd', props: { invitation: getTable()?.getSelectedData?.()?.[0], type: 'add' }, mask: 'trng028' });
  }
  function editInvitation() {
    dispatch({ type: 'create_new_tab', name: t('TrainingInvitationList.Tab.Edit'), translationKey: 'TrainingInvitationList.Tab.Edit', legacyLabel: t('TrainingInvitationList.Tab.Edit'), page: 'TrainingInvitationAdd', props: { invitation: getTable()?.getSelectedData?.()?.[0], type: 'edit' }, mask: 'trng028' });
  }

  function setMask(item, value) {
    if (!Object.prototype.hasOwnProperty.call(DEFAULT_COLUMNS_VISIBLE, item)) return;
    const next = { ...DEFAULT_COLUMNS_VISIBLE, ...columnsVisible, [item]: value };
    if (!Object.values(next).some(Boolean)) return;
    applyDataColumnVisibilityToTable(getTable(), next);
    setColumnsVisible(next);
  }

  const itemRenderer = (item, options) => (
    <a className="flex align-items-center px-3 py-2 cursor-pointer items-center bg-[#333] text-white w-[180px]" onClick={options.onClick}>
      <span className={`${item.icon} text-primary`} />
      <span className={`mx-2 ${item.items && 'font-semibold'}`}>{item.label}</span>
      {item.shortcut && <span className="ml-auto border-1 surface-border border-round surface-100 text-xs p-1">{item.shortcut}</span>}
    </a>
  );

  const [expandedKeys, setExpandedKeys] = useState({});

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (overlayRef.current && !overlayRef.current.contains(event.target)) setExpandedKeys({});
    };
    document.addEventListener('click', handleClickOutside, true);
    return () => document.removeEventListener('click', handleClickOutside, true);
  }, []);

  const resetMask = async () => {
    await resetTabulatorMask({
      page: PAGE_LIST_KEY,
      defaultColumnsVisible: { ...DEFAULT_COLUMNS_VISIBLE },
      ref,
      columns,
      setColumnsVisible,
      onAfterReset: () => requestFullProgressiveSessionRestart(),
    });
  };

  const items = [
    {
      key: '0',
      label: t('Common.Actions'),
      icon: 'pi pi-sliders-v',
      template: itemRenderer,
      items: [
        ...(!preview && !preview_period ? [
          {
            key: '0_1',
            label: t('Common.SetFilter'),
            icon: 'pi pi-filter',
            command: () => { userInteractedRef.current = true; setUserMaskFilter(PAGE_LIST_KEY, 'set_filters', null, getTable()?.getHeaderFilters?.() ?? [], null); requestFullProgressiveSessionRestart(); },
            template: itemRenderer,
          },
          {
            key: '0_2',
            label: t('Common.ClearFilter'),
            icon: 'pi pi-filter-slash',
            command: () => { userInteractedRef.current = true; setUserMaskFilter(PAGE_LIST_KEY, 'set_filters', null, [], null); getTable()?.clearHeaderFilter?.(); requestFullProgressiveSessionRestart(); },
            template: itemRenderer,
          },
        ] : []),
        {
          key: '0_3',
          label: t('Common.SetSorting'),
          icon: 'pi pi-sort-alt',
          command: () => { userInteractedRef.current = true; setUserMaskFilter(PAGE_LIST_KEY, 'set_sorters', null, null, null, cleanSorters(getTable()?.getSorters?.() ?? [])); OverflowMenuProvider.setVisible(null); setExpandedKeys({}); requestFullProgressiveSessionRestart(); },
          template: itemRenderer,
        },
        {
          key: '0_4',
          label: t('Common.ClearSorting'),
          icon: 'pi pi-sort-alt-slash',
          command: () => { userInteractedRef.current = true; setUserMaskFilter(PAGE_LIST_KEY, 'set_sorters', null, null, null, []); getTable()?.setSort?.([]); OverflowMenuProvider.setVisible(null); setExpandedKeys({}); requestFullProgressiveSessionRestart(); },
          template: itemRenderer,
        },
        {
          key: '0_5',
          label: t('Common.SetMask'),
          icon: 'pi pi-server',
          command: () => { setMaskVisible(true); OverflowMenuProvider.setVisible(null); setExpandedKeys({}); },
          template: itemRenderer,
        },
        {
          key: '0_5b',
          label: t('Common.ResetUserMask'),
          icon: 'pi pi-refresh',
          command: () => { resetMask(); OverflowMenuProvider.setVisible(null); setExpandedKeys({}); },
          template: itemRenderer,
        },
        {
          key: '0_6',
          label: t('Common.Notifications'),
          icon: 'pi pi-envelope',
          command: () => { openChosenNotificationPreferences(PAGE_LIST_KEY); OverflowMenuProvider.setVisible(null); },
          template: itemRenderer,
        },
      ],
    },
  ];

  function changeMarkings(value) {
    const next = { ...columnsVisible };
    Object.keys(DEFAULT_COLUMNS_VISIBLE).forEach((val) => {
      if (val === 'id') return;
      next[val] = value;
    });
    applyDataColumnVisibilityToTable(getTable(), next);
    setColumnsVisible(next);
  }

  return (
    <>
      <div className='w-full h-10 flex flex-row bg-[#333]'>
        <IntersectionObserverWrapper>
          <div className='flex flex-col w-10 h-10 px-2 py-1 justify-center' data-targetid="add"><button type="button" onClick={() => addNewInvitation()}><AddIcon /></button></div>
          <div className='flex flex-col w-10 h-10 px-2 py-1 justify-center' data-targetid="edit"><button type="button" className="disabled:opacity-25" disabled={editIcon} onClick={() => editInvitation()}><EditIcon /></button></div>
          <div className='flex flex-col w-10 h-10 px-2 py-1 justify-center' data-targetid="copy"><button type="button" className="disabled:opacity-25" disabled={copyIcon} onClick={() => copyInvitation()}><CopyIcon /></button></div>
          <div className='flex flex-col w-10 h-10 px-2 py-1 justify-center' data-targetid="delete"><button type="button" className="disabled:opacity-25" onClick={confirm} disabled={deleteIcon}><DeleteIcon /></button></div>
          <div className='flex flex-col w-10 h-10 px-2 py-1 justify-center' data-targetid="print"><button type="button" title={t('Common.Print')} onClick={() => setPrintChoiceVisible(true)}><PrintIcon /></button></div>
          <div className='flex flex-col w-10 h-10 px-2 py-1 justify-center' data-targetid="search"><button type="button" title={searchIconColor !== 'active' ? t('Common.Search') : t('TrainingInvitationList.CancelSearch')} onClick={searchIconColor !== 'active' ? () => setVisibleSearch(true) : () => clearSearch()}>{searchIconColor === '' ? <MdSearch fill='#fff' strokeWidth="0" className='w-[28px] h-[28px]' aria-hidden="true" title={t('Common.Search')} /> : <MdSearchOff fill='#fff' strokeWidth="0" className='w-[28px] h-[28px]' aria-hidden="true" title={t('TrainingInvitationList.CancelSearch')} />}</button></div>
          <div className='flex flex-col w-10 h-10 px-2 py-1 justify-center' data-targetid="export"><button type="button" data-testid="toolbar-export" title={t('Common.ExportToExcel')} onClick={() => exportInvitationsXlsx()}><ExportIcon /></button></div>
          <div className='flex flex-col w-10 h-10 px-2 py-1 justify-center' data-targetid="refresh"><button type="button" onClick={() => { userInteractedRef.current = true; requestFullProgressiveSessionRestart(); }} title={t('Common.Refresh')}><RefreshIcon /></button></div>
          <div className='flex flex-col w-10 h-10 px-2 py-1 justify-center' data-targetid="vert"><VerticalLineIcon /></div>
          <div className='flex flex-col w-10 h-10 px-2 py-1 justify-center' data-targetid="first"><button type="button" title={t('Icon.First')} onClick={() => scrollToFirst()}><BackToFirstIcon /></button></div>
          <div className='flex flex-col w-10 h-10 px-2 py-1 justify-center' data-targetid="back"><button type="button" title={t('Icon.Previous')} onClick={() => scrollUp()}><BackIcon /></button></div>
          <div className='flex flex-col w-10 h-10 px-2 py-1 justify-center' data-targetid="forward"><button type="button" title={t('Icon.Next')} onClick={() => scrollDown()}><ForwardIcon /></button></div>
          <div className='flex flex-col w-10 h-10 px-2 py-1 justify-center' data-targetid="last"><button type="button" title={t('Icon.Last')} onClick={() => scrollToLast()}><ForwardToLastIcon /></button></div>
          <div className='flex flex-col w-10 h-10 px-2 py-1 justify-center' data-targetid="vert"><VerticalLineIcon /></div>
          <div className='flex flex-col w-10 h-10 px-2 py-1 justify-center' data-targetid="favorite"><button type="button" title={t('Common.AddToFavorites')} className="disabled:opacity-25" disabled={favoriteIcon} onClick={() => openFavoriteDialog(t('TrainingInvitationList.FavoriteTitle'), PAGE_LIST_KEY)}><FavoriteIcon /></button></div>
          <div className='flex flex-col w-10 h-10 px-2 py-1 justify-center' data-targetid="fast"><button type="button" title={t('Common.QuickView')} onClick={() => setFastView(PAGE_LIST_KEY, columnsVisible, columns)}><PriceIcon /></button></div>
          <div className='flex flex-col w-10 h-10 px-2 py-1 justify-center' data-targetid="vert"><VerticalLineIcon /></div>
          <div className="card flex justify-content-center z-[100]" data-targetid="actions" ref={overlayRef}>
            <PanelMenu model={items} expandedKeys={expandedKeys} onExpandedKeysChange={setExpandedKeys} className="w-full md:w-20rem" />
          </div>
        </IntersectionObserverWrapper>
      </div>
      <div className='px-1 bg-white rounded-md shadow-md'>
        <div className="flex flex-col overflow-x-auto no-scrollbar min-h-[calc(100vh-177px)]">
          <div className="overflow-hidden">
            <div className="inline-block w-full py-2">
              <div ref={tableContainerRef}>
                <ReactTabulator
                  key={tableSessionKey}
                  data={undefined}
                  className='h-[calc(100vh_-_193px)]'
                  onRef={(r) => (ref.current = r?.current)}
                  columns={columns}
                  events={{
                    rowClick,
                    rowSelectionChanged: onSelectRow,
                    tableBuilt: () => {
                      const table = getTable();
                      if (!table) return;
                      const restartRestore = sessionQueryActiveRef.current;
                      try {
                        table.blockRedraw?.();
                        const effective = getEffectiveMask(PAGE_LIST_KEY, state);
                        if (effective?.type === 'user' && effective.userMask) {
                          const attributes = sanitizeTabulatorLayoutAttributes(safeParse(effective.userMask.attributes, []), TABULATOR_GROUP_A_FLAT_REMOTE_LAYOUT_SANITIZE_OPTIONS);
                          const jsonColumns = safeParse(effective.userMask.columns, {});
                          if (layoutAttributesIncludeId(attributes) && attributes.length > 1) {
                            try { table.setColumnLayout?.(attributes); } catch (_) {}
                          }
                          if (jsonColumns && typeof jsonColumns === 'object') {
                            const merged = mergeVisibility(jsonColumns);
                            setColumnsVisible(merged);
                            applyDataColumnVisibilityToTable(table, merged);
                          }
                          if (restartRestore) restoreHeaderFilters(table, confirmedQueryRef.current.filters);
                          else {
                            const filters = parseMaskFilters(effective.userMask.filters);
                            const sorters = parseSorters(effective.userMask.sorters);
                            restoreHeaderFilters(table, filters);
                            if (Array.isArray(sorters) && sorters.length > 0) {
                              try { table.setSort?.(sorters); } catch (_) {}
                            }
                          }
                        } else if (effective?.type === 'standard' && effective.standardMask) {
                          const attributes = sanitizeTabulatorLayoutAttributes(safeParse(effective.standardMask.attributes, []), TABULATOR_GROUP_A_FLAT_REMOTE_LAYOUT_SANITIZE_OPTIONS);
                          const jsonColumns = safeParse(effective.standardMask.columns, {});
                          if (layoutAttributesIncludeId(attributes) && attributes.length > 1) {
                            try { table.setColumnLayout?.(attributes); } catch (_) {}
                          }
                          if (jsonColumns && typeof jsonColumns === 'object') {
                            const merged = mergeVisibility(jsonColumns);
                            setColumnsVisible(merged);
                            applyDataColumnVisibilityToTable(table, merged);
                          }
                          if (restartRestore) restoreHeaderFilters(table, confirmedQueryRef.current.filters);
                        } else {
                          applyDataColumnVisibilityToTable(table, DEFAULT_COLUMNS_VISIBLE);
                          if (restartRestore) restoreHeaderFilters(table, confirmedQueryRef.current.filters);
                        }
                      } finally {
                        table.restoreRedraw?.(true);
                      }
                      tableBuiltRef.current = true;
                      restartQueuedRef.current = false;
                      setTimeout(() => {
                        isRestoringRef.current = false;
                        sessionQueryActiveRef.current = false;
                      }, 0);
                    },
                    headerFilterChanged: () => {
                      if (isRestoringRef.current) return;
                      userInteractedRef.current = true;
                      requestFullProgressiveSessionRestart();
                    },
                    sortChanged: () => {
                      if (!isRestoringRef.current) {
                        userInteractedRef.current = true;
                        requestFullProgressiveSessionRestart();
                      }
                    },
                    columnMoved: () => {
                      if (!userInteractedRef.current || isRestoringRef.current) return;
                      setUserMaskFilter(PAGE_LIST_KEY, 'column_move', null, null, getTable()?.getColumnLayout?.());
                    },
                    columnResized: () => {
                      if (!userInteractedRef.current || isRestoringRef.current) return;
                      setUserMaskFilter(PAGE_LIST_KEY, 'column_resized', null, null, getTable()?.getColumnLayout?.());
                    },
                    renderComplete: () => restoreHScroll(),
                  }}
                  options={{
                    index: 'id',
                    renderHorizontal: 'virtual',
                    layout: 'fitColumns',
                    autoResize: true,
                    rowHeight: TABULATOR_AUDIT_MIN_ROW_HEIGHT,
                    movableColumns: true,
                    headerSortTristate: true,
                    ...(cleanSorters(confirmedQueryRef.current.sorters || []).length > 0
                      ? {
                        initialSort: cleanSorters(confirmedQueryRef.current.sorters).map((sorter) => ({
                          column: sorter.field,
                          dir: sorter.dir,
                        })),
                      }
                      : {}),
                    ajaxURL: '/api/training_invitations/paged',
                    ajaxParams: () => ({}),
                    ajaxRequestFunc: (url, config, params) => {
                      const generation = ajaxGenerationRef.current;
                      return createDedupedAjaxRequest(getBuildRequestParams, tabulatorGet, inFlightRef, completedCacheRef, isRestoringRef, lastResponseRef)(url, config, params).then((data) => {
                        if (generation !== ajaxGenerationRef.current) {
                          return { data: [], last_page: data?.last_page ?? 1, total: 0, current_page: Number(params?.page) || 1 };
                        }
                        return data;
                      });
                    },
                    dataLoader: true,
                    dataLoaderLoading: t('Common.LoadingData'),
                    dataLoaderError: t('Common.LoadFailed'),
                    placeholder: t('Common.NoData'),
                    height: 'calc(100vh - 193px)',
                    progressiveLoad: 'scroll',
                    progressiveLoadScrollMargin: 80,
                    progressiveLoadDelay: 200,
                    paginationSize: pageSizePc,
                    filterMode: 'remote',
                    sortMode: 'remote',
                    pagination: false,
                    ajaxRequesting: () => {
                      if (tableBuiltRef.current && !isRestoringRef.current) captureHScroll();
                      const table = getTable();
                      if (table?.initialized) table.alert?.(t('Common.LoadingData'));
                      return true;
                    },
                    ajaxResponse: (url, params, response) => {
                      getTable()?.clearAlert?.();
                      const next = applyPagedMutationOverlay(response, mutationOverlayRef.current);
                      if (next && typeof next === 'object') lastResponseRef.current = next;
                      return next;
                    },
                    ajaxError: () => {
                      getTable()?.clearAlert?.();
                    },
                    scrollToColumnIfVisible: false,
                  }}
                />
              </div>
            </div>
          </div>
        </div>
        <Dialog header={t('Common.SearchInList')} visible={visibleSearch} style={{ width: '40vw' }} onHide={() => setVisibleSearch(false)}>
          <div className="flex flex-columns">
            <input
              value={searchValue}
              onChange={(e) => setSearchValue(e.target.value)}
              type="text"
              className="block w-full px-4 py-2 text-grey-700 bg-white border rounded-md focus:border-gray-400 focus:ring-gray-300 focus:outline-none focus:ring focus:ring-opacity-40"
            />
            <button type="button" onClick={() => searchTable()} className='w-16 ml-8 px-4 py-2 tracking-wide text-white transition-colors duration-200 transform bg-zinc-900 rounded-md hover:bg-zinc-700 focus:outline-none focus:bg-zinc-600'><SearchIcon /></button>
          </div>
        </Dialog>
        <Dialog header={t('Common.SetMask')} visible={maskVisible} style={{ width: '70vw' }} onHide={() => setMaskVisible(false)}>
          <div className="flex flex-col">
            <button type="button" onClick={() => { changeMarkings(!markUnmark); setMarkUnmarked(!markUnmark); }} className='m-1 px-4 py-2 text-center tracking-wide text-white transition-colors duration-200 transform bg-zinc-700 rounded-md hover:bg-zinc-500'>{markUnmark ? t('Common.DeselectAll') : t('Common.SelectAll')}</button>
            {columns.map(function (item, index) {
              if (!item?.field || !Object.prototype.hasOwnProperty.call(DEFAULT_COLUMNS_VISIBLE, item.field)) return null;
              return (
                <div key={item.field ?? index} className="flex flex-row border border-black rounded-md p-2 m-1">
                  <Checkbox onChange={(e) => setMask(item.field, e.checked)} checked={columnsVisible[item.field]}></Checkbox><span className="ml-2">{item.title}</span>
                </div>
              );
            })}
            <button type="button" onClick={() => { setUserMaskFilter(PAGE_LIST_KEY, 'set_columns', columnsVisible, null, null); setMaskVisible(false); }} className='m-1 px-4 py-2 text-center tracking-wide text-white transition-colors duration-200 transform bg-zinc-900 rounded-md hover:bg-zinc-700 focus:outline-none focus:bg-zinc-600'>{t('Common.Save')}</button>
          </div>
        </Dialog>
        <PrintOptionsDialog
          visible={printChoiceVisible}
          onHide={() => setPrintChoiceVisible(false)}
          orientation={printOrientationChoice}
          onOrientationChange={setPrintOrientationChoice}
          layout={printLayoutChoice}
          onLayoutChange={setPrintLayoutChoice}
          onConfirm={runInvitationPrint}
        />
      </div>
    </>
  );
}
